/**
 * @fileoverview Shared lock-state and heartbeat contract for co-inspection.
 * @module sync-config/coInspection
 *
 * Responsibilities:
 * - Define the server-to-client shape of an active co-inspection lock.
 * - Share heartbeat timing constants and lock normalization across API and web packages.
 * - Merge co-inspection responses by field key and timestamp.
 * - Evaluate heartbeat freshness without I/O or host dependencies.
 *
 * Dependencies:
 * - None.
 *
 * Architecture:
 * - Pure contract and time helper exported by `@firesquirrel/sync-config`.
 * - Mobile API produces this state and mobile/web consumers use the same
 *   freshness policy.
 *
 * Notes:
 * - A missing or invalid heartbeat is offline.
 * - Device identifiers and heartbeat values are nullable for legacy locks.
 */

/**
 * @contract CoInspectionPrimaryState
 * Purpose: Primary inspector identity and live heartbeat state.
 * Fields: userId, authId, userName, deviceId, lastHeartbeatAt, isOnline.
 * Rules: Missing device or heartbeat values remain explicit as null.
 * Sync: Returned in the shared co-inspection lock state.
 */
export interface CoInspectionPrimaryState {
  userId: string;
  authId: string | null;
  userName: string;
  deviceId: string | null;
  lastHeartbeatAt: string | null;
  isOnline: boolean;
}

/**
 * @contract CoInspectionSecondaryState
 * Purpose: Secondary inspector identity and live heartbeat state.
 * Fields: userId, authId, userName, deviceId, joinedAt, lastHeartbeatAt, isOnline.
 * Rules: Rejoining preserves the original joinedAt timestamp.
 * Sync: Returned in the shared co-inspection lock state.
 */
export interface CoInspectionSecondaryState {
  userId: string;
  authId: string | null;
  userName: string;
  deviceId: string | null;
  joinedAt: string;
  lastHeartbeatAt: string | null;
  isOnline: boolean;
}

/**
 * @contract CoInspectionLockState
 * Purpose: Shared active lock state consumed by mobile, tenant, and sync policy.
 * Fields: lockId, seriesId, expiresAt, primary, secondaryInspectors.
 * Rules: Top-level legacy lock fields remain the persisted primary source.
 * Sync: Presence state is returned by lock routes and is not case-roster data.
 */
export interface CoInspectionLockState {
  lockId: string;
  seriesId: string;
  expiresAt: string;
  primary: CoInspectionPrimaryState;
  secondaryInspectors: CoInspectionSecondaryState[];
}

/**
 * @process coInspectionParticipantMatches
 * Steps: Compare a non-empty subject identity with either participant identity.
 * Inputs: Lock participant and subject ID.
 * Outputs: True when the subject matches the Mongo user ID or external auth ID.
 * Failure Handling: Empty subject IDs never match.
 * Idempotency: REQUIRED.
 */
export function coInspectionParticipantMatches(
  participant: Pick<CoInspectionPrimaryState | CoInspectionSecondaryState, 'userId' | 'authId'>,
  subjectId: string | null | undefined,
): boolean {
  return Boolean(subjectId && (participant.userId === subjectId || participant.authId === subjectId));
}

/** Heartbeat cadence used by active co-inspection participants. */
export const CO_INSPECTION_HEARTBEAT_INTERVAL_MS = 30_000;

/** Maximum age of a heartbeat for a participant to be considered online. */
export const CO_INSPECTION_ONLINE_WINDOW_MS = 90_000;

/**
 * @process isCoInspectorHeartbeatFresh
 * Steps: Parse the heartbeat and compare its age with the shared online window.
 * Inputs: Heartbeat timestamp and current epoch time in milliseconds.
 * Outputs: True only for a valid, non-future heartbeat within the online window.
 * Failure Handling: Missing, invalid, or non-finite values return false.
 * Idempotency: REQUIRED.
 */
export function isCoInspectorHeartbeatFresh(
  lastHeartbeatAt: string | Date | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (lastHeartbeatAt == null || !Number.isFinite(nowMs)) return false;
  const heartbeatMs = lastHeartbeatAt instanceof Date
    ? lastHeartbeatAt.getTime()
    : Date.parse(lastHeartbeatAt);
  if (!Number.isFinite(heartbeatMs)) return false;
  const ageMs = nowMs - heartbeatMs;
  return ageMs >= 0 && ageMs <= CO_INSPECTION_ONLINE_WINDOW_MS;
}

function toTimestamp(value: unknown): string | null {
  if (value instanceof Date && Number.isFinite(value.getTime())) return value.toISOString();
  if (typeof value === 'string' && Number.isFinite(Date.parse(value))) return new Date(value).toISOString();
  return null;
}

/**
 * @process toCoInspectionLockState
 * Steps: Normalize a persisted inspection lock and derive online status for every participant.
 * Inputs: Persisted lock document and the current epoch time in milliseconds.
 * Outputs: Shared lock state suitable for API responses and sync authorization.
 * Failure Handling: Missing or invalid participant timestamps are represented as offline.
 * Idempotency: REQUIRED.
 */
export function toCoInspectionLockState(
  lock: Record<string, any>,
  nowMs = Date.now(),
): CoInspectionLockState {
  const primaryHeartbeat = toTimestamp(lock.lastHeartbeatAt);
  const secondaryInspectors = Array.isArray(lock.secondaryInspectors) ? lock.secondaryInspectors : [];
  return {
    lockId: String(lock._id),
    seriesId: String(lock.seriesId ?? ''),
    expiresAt: toTimestamp(lock.expiresAt) ?? '',
    primary: {
      userId: String(lock.userId ?? ''),
      authId: typeof lock.authId === 'string' ? lock.authId : null,
      userName: String(lock.userName ?? 'Unknown'),
      deviceId: typeof lock.deviceId === 'string' ? lock.deviceId : null,
      lastHeartbeatAt: primaryHeartbeat,
      isOnline: isCoInspectorHeartbeatFresh(primaryHeartbeat, nowMs),
    },
    secondaryInspectors: secondaryInspectors.map((secondary: Record<string, any>) => {
      const lastHeartbeatAt = toTimestamp(secondary.lastHeartbeatAt);
      return {
        userId: String(secondary.userId ?? ''),
        authId: typeof secondary.authId === 'string' ? secondary.authId : null,
        userName: String(secondary.userName ?? 'Unknown'),
        deviceId: typeof secondary.deviceId === 'string' ? secondary.deviceId : null,
        joinedAt: toTimestamp(secondary.joinedAt) ?? '',
        lastHeartbeatAt,
        isOnline: isCoInspectorHeartbeatFresh(lastHeartbeatAt, nowMs),
      };
    }),
  };
}

/**
 * @process mergeCoInspectionResponses
 * Steps: Resolve overlapping field values by timestamp and reconcile server-only fields against the incoming document timestamp.
 * Inputs: Existing and incoming response maps keyed by fieldId, plus an optional incoming case updatedAt timestamp.
 * Outputs: A new response map with the winning field values and any unseen server writes.
 * Failure Handling: Invalid field timestamps defer to the only valid side; incoming wins ties or when neither is valid. Missing or invalid document timestamps preserve the legacy union.
 * Idempotency: REQUIRED.
 */
export function mergeCoInspectionResponses<T extends Record<string, unknown>>(
  existing: Record<string, T> | null | undefined,
  incoming: Record<string, T> | null | undefined,
  incomingDocUpdatedAt?: string,
): Record<string, T> {
  const existingResponses = existing ?? {};
  const incomingResponses = incoming ?? {};
  const incomingIds = new Set(Object.keys(incomingResponses));
  const documentUpdatedAt = toValidTimestamp(incomingDocUpdatedAt);
  const merged: Record<string, T> = {};

  for (const [fieldId, existingResponse] of Object.entries(existingResponses)) {
    if (incomingIds.has(fieldId)) continue;
    const existingUpdatedAt = toValidTimestamp(existingResponse.updatedAt);
    if (documentUpdatedAt === null || (existingUpdatedAt !== null && existingUpdatedAt > documentUpdatedAt)) {
      merged[fieldId] = existingResponse;
    }
  }

  for (const [fieldId, incomingResponse] of Object.entries(incomingResponses)) {
    const existingResponse = existingResponses[fieldId];
    if (!existingResponse) {
      merged[fieldId] = incomingResponse;
      continue;
    }
    const incomingUpdatedAt = toValidTimestamp(incomingResponse.updatedAt);
    const existingUpdatedAt = toValidTimestamp(existingResponse.updatedAt);
    if (
      (incomingUpdatedAt !== null && existingUpdatedAt === null)
      || (incomingUpdatedAt !== null && existingUpdatedAt !== null && incomingUpdatedAt >= existingUpdatedAt)
      || (incomingUpdatedAt === null && existingUpdatedAt === null)
    ) {
      merged[fieldId] = incomingResponse;
    } else {
      merged[fieldId] = existingResponse;
    }
  }
  return merged;
}

function toValidTimestamp(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}
